'use client';

import { ThreadLoadingColumn } from '@/app/[orgSlug]/threads/[id]/thread-loading-column';
import { DockedArtifactPanelSplit } from '@/components/org-chat/interface/presentation/docked-artifact-panel-reservation';
import { ThreadsSidebarSkeleton } from '@/app/[orgSlug]/threads/thread-sidebar-skeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { THREAD_SIDEBAR_OFFSET_BOOT_VAR } from '@/lib/thread-sidebar-offset-var';
import type { WorkspaceAdmissionShellVariant } from './workspace-admission-shell-variant';

const SIDEBAR_WIDTH = `var(${THREAD_SIDEBAR_OFFSET_BOOT_VAR}, 288px)`;

/**
 * Placeholder for the workspace membership boundary while it admits the user.
 *
 * Purely presentational: it mounts no workspace children, providers, queries
 * or effects, and shows no private content (no workspace names, titles or
 * files). It only mirrors the page frame (the default thread sidebar column
 * at its pre-paint width, and a skeleton for the route's content) so the real
 * layout lands in place instead of replacing a full-screen spinner.
 */
export function WorkspaceAdmissionShell({
  variant,
}: {
  variant: Exclude<WorkspaceAdmissionShellVariant, 'generic'>;
}) {
  return (
    <div
      data-workspace-admission-shell={variant}
      aria-hidden="true"
      className="workspace-canvas-surface flex h-dvh flex-col overflow-hidden"
    >
      <div className="flex-1">
        <aside
          className="thread-sidebar-surface fixed top-0 bottom-0 left-0 hidden lg:block"
          style={{ width: SIDEBAR_WIDTH }}
        >
          <ThreadsSidebarSkeleton />
        </aside>
        <main className="workspace-canvas-surface h-full overflow-hidden lg:ml-[var(--thread-sidebar-offset-boot,240px)]">
          {variant === 'thread' ? (
            <DockedArtifactPanelSplit>
              <ThreadLoadingColumn />
            </DockedArtifactPanelSplit>
          ) : (
            <DesignHomeSkeleton />
          )}
        </main>
      </div>
    </div>
  );
}

function DesignHomeSkeleton() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-4">
      <Skeleton className="h-9 w-72 max-w-full rounded-md" />
      <Skeleton className="h-24 w-full max-w-2xl rounded-[30px]" />
    </div>
  );
}
